import { isEqualWith } from 'lodash';
import {
    BundleSizeAlertNotifyFor,
    CommonNotification,
    FixRequestReviewNotifyFor,
    NotificationDestination,
    NotificationFrequency,
    RelativeTiming,
    ReminderOffset,
    StepNotificationConfigOverride,
} from '../types';
import {
    BrokenIntegrationFilterMode,
    BrokenIntegrationNotifyMode,
    DEFAULT_BROKEN_INTEGRATION_DURATION_UNIT,
    DEFAULT_BROKEN_INTEGRATION_DURATION_VALUE,
} from '../types/notifications';
import { dayjs } from '@/extendedDayjs';

export const hourOfDayFromFormValues = (values: Record<string, unknown>): number | null =>
    values.hourOfDay ?
        dayjs(values.hourOfDay as string, 'HH:mm')
            .clone()
            .utc()
            .hour()
    :   null;

// Condition groups need monitoring metrics to convert, so callers convert those first.
export const pendingSettingsFromFormValues = (values: Record<string, unknown>): Record<string, unknown> =>
    'hourOfDay' in values ? { ...values, hourOfDayForNotification: hourOfDayFromFormValues(values) } : { ...values };

export enum NotificationDrawerSection {
    Settings = 'settings',
    Channels = 'channels',
    Triggers = 'triggers',
}

export const isEmptyFormValue = (value: unknown) =>
    value === undefined ||
    value === null ||
    value === '' ||
    value === false ||
    (Array.isArray(value) && value.length === 0);

export const looseFormValueCompare = (a: unknown, b: unknown) => {
    if ((typeof a === 'string' && typeof b === 'number') || (typeof a === 'number' && typeof b === 'string')) {
        return a == b;
    }
    if ((a === null && b === '') || (a === '' && b === null)) {
        return true;
    }
    if ((a === undefined && b === null) || (a === null && b === undefined)) {
        return true;
    }
    return undefined;
};

export interface NotificationSectionMeta {
    key: NotificationDrawerSection;
    fieldNames: string[];
}

export interface NotificationOverrideEntry {
    key: string;
    stepConfigIds: string[];
    savedStepConfigIds: string[];
    groupName: string;
    savedGroupName: string;
    override: StepNotificationConfigOverride | null;
    saved: boolean;
}

export interface SaveNotificationOverridePayload {
    stepConfigIds: string[];
    deletedStepConfigIds: string[];
    groupName: string;
    values: Record<string, unknown>;
    /** [] = app-level channels */
    channels: string[];
    usePrimaryChannels: boolean;
    overriddenFields: string[];
}

export const SETTINGS_KEY_BY_FORM_FIELD: Record<string, string> = {
    conditionGroupsField: 'conditionGroups',
    hourOfDay: 'hourOfDayForNotification',
};

// Overrides saved before overriddenFields hold whole form snapshots, defaults included, while the API leaves them unset.
export const NOTIFICATION_FORM_DEFAULTS = {
    notificationChannels: [NotificationDestination.Slack],
    notificationFrequency: NotificationFrequency.Weekly,
    appleProfileAndCertificateReminderDaysBeforeExpiration: 14,
    reminderOffsets: [
        { days: 0, hours: 24, minutes: 0, timing: RelativeTiming.before },
        { days: 0, hours: 0, minutes: 30, timing: RelativeTiming.before },
    ] as ReminderOffset[],
    bundleSizeAlertNotifyFor: BundleSizeAlertNotifyFor.FirstBuildOnly,
    fixRequestReviewNotifyFor: FixRequestReviewNotifyFor.ApprovalOrRejectionOnly,
    includePRCommitInfo: true,
    includeFixRequestDetails: true,
    brokenIntegrationNotifyMode: BrokenIntegrationNotifyMode.All,
    brokenIntegrationFilterMode: BrokenIntegrationFilterMode.Include,
    brokenIntegrationDurationValue: DEFAULT_BROKEN_INTEGRATION_DURATION_VALUE,
    brokenIntegrationDurationUnit: DEFAULT_BROKEN_INTEGRATION_DURATION_UNIT,
};

// false is a real value here (e.g. a default-true toggle turned off), unlike isEmptyFormValue.
const isUnsetValue = (value: unknown) =>
    value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

const withFormDefault = (key: string, value: unknown) =>
    isUnsetValue(value) && key in NOTIFICATION_FORM_DEFAULTS ?
        NOTIFICATION_FORM_DEFAULTS[key as keyof typeof NOTIFICATION_FORM_DEFAULTS]
    :   value;

const overrideSettingDiffers = (
    settings: Record<string, unknown>,
    appNotification: CommonNotification,
    key: string,
): boolean => {
    if (!(key in settings)) {
        return false;
    }
    const overrideValue = withFormDefault(key, settings[key]);
    const appValue = withFormDefault(key, appNotification[key]);
    if (isEmptyFormValue(overrideValue) && isEmptyFormValue(appValue)) {
        return false;
    }
    return !isEqualWith(overrideValue, appValue, looseFormValueCompare);
};

const TRIGGER_SETTINGS_KEYS = [
    'adoptionThreshold',
    'adoptionThresholds',
    'phasedReleaseDayThreshold',
    'conditionGroups',
    'hourOfDayForNotification',
    'notificationFrequency',
    'reminderOffsets',
    'appleProfileAndCertificateReminderDaysBeforeExpiration',
    'selectedProfileAndCertificateTypes',
    'bundleSizeIncreaseConditions',
    'bundleSizeCellularDownloadLimitThresholdMB',
    'bundleSizeAlertNotifyFor',
    'fixRequestReviewNotifyFor',
    'brokenIntegrationDurationValue',
    'brokenIntegrationDurationUnit',
];

// Mentions are one choice (the backend merge applies them together).
const MENTION_CHOICE_KEYS = ['mentionUsers', 'mentionGroups', 'mentionReleasePilot'];

// Condition groups replace these; the backend merge drops them too.
const LEGACY_THRESHOLD_KEYS = ['adoptionThreshold', 'adoptionThresholds', 'phasedReleaseDayThreshold'];

const MENTION_SETTINGS_KEYS = ['mentionGroups', 'mentionUsers', 'mentionReleasePilot', 'emailsToInclude'];

const DESTINATION_SETTINGS_KEYS = ['notificationChannels'];

const CATEGORIZED_KEYS = new Set([
    'channels',
    ...DESTINATION_SETTINGS_KEYS,
    ...MENTION_SETTINGS_KEYS,
    ...TRIGGER_SETTINGS_KEYS,
]);

const SETTING_LABELS: Record<string, string> = {
    customCopy: 'Custom text',
    includeNewIssuesIdentifiedByRunway: 'Include new stability issues',
    threadWithRelatedNotifications: 'Thread related notifications',
    alsoSendThreadedToChannel: 'Also send thread replies to the channel',
    threadHealthMetrics: 'Move health metrics into a thread reply',
    includeTicketListInSubmitNotifications: 'Include ticket list',
    includeDirectDownloadLink: 'Include direct download links',
    directDownloadLinkAccessLimit: 'Max downloads',
    directDownloadLinkExpiryHours: 'Download link expiry',
    includePROwnerInCherryPickNotifications: 'Include PR owner',
    includePRCommitInfo: 'Include PR/commit info',
    includeFixRequestDetails: 'Include fix details',
    includeDraftPRs: 'Include fix requests on draft PRs',
    includeCommentsInRegressionItemNotification: 'Include comments',
    brokenIntegrationNotifyMode: 'Integrations to notify about',
    brokenIntegrationFilterMode: 'Integrations to notify about',
    brokenIntegrationInstallationIds: 'Integrations to notify about',
};

const settingLabel = (key: string): string => {
    if (SETTING_LABELS[key]) {
        return SETTING_LABELS[key];
    }
    const words = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
};

export const overrideDiffersOnKey = (
    override: StepNotificationConfigOverride,
    appNotification: CommonNotification,
    key: string,
): boolean =>
    override.overriddenFields ?
        override.overriddenFields.includes(key)
    :   overrideSettingDiffers(override.settings ?? {}, appNotification, key);

// Primary only differs from app level when the notification has its own channels.
const sendsToPrimaryChannels = (usePrimaryChannels: boolean, appNotification: CommonNotification) =>
    usePrimaryChannels && (appNotification.channels?.length ?? 0) > 0;

export const overrideSendsToPrimaryChannels = (
    override: StepNotificationConfigOverride,
    appNotification: CommonNotification,
): boolean => sendsToPrimaryChannels(!!override.usePrimaryChannels, appNotification);

export const overrideHasChannelDifference = (
    override: StepNotificationConfigOverride,
    appNotification: CommonNotification,
): boolean => {
    if (overrideSendsToPrimaryChannels(override, appNotification)) {
        return true;
    }
    if (override.overriddenFields) {
        return override.overriddenFields.some((key) => key === 'channels' || DESTINATION_SETTINGS_KEYS.includes(key));
    }
    if (DESTINATION_SETTINGS_KEYS.some((key) => overrideDiffersOnKey(override, appNotification, key))) {
        return true;
    }
    const overrideIds = (override.channels ?? []).map((channel) => channel.id).sort();
    if (overrideIds.length === 0) {
        return false;
    }
    const appIds = (appNotification.channels ?? []).map((channel) => channel.id).sort();
    return overrideIds.length !== appIds.length || overrideIds.some((id, index) => id !== appIds[index]);
};

export const overrideHasMentionDifference = (
    override: StepNotificationConfigOverride,
    appNotification: CommonNotification,
): boolean => MENTION_SETTINGS_KEYS.some((key) => overrideDiffersOnKey(override, appNotification, key));

export const hasTriggerOverride = (
    override: StepNotificationConfigOverride,
    appNotification: CommonNotification,
): boolean => TRIGGER_SETTINGS_KEYS.some((key) => overrideDiffersOnKey(override, appNotification, key));

export const overriddenSettingKeys = (
    override: StepNotificationConfigOverride,
    appNotification: CommonNotification,
): string[] =>
    (override.overriddenFields ?? Object.keys(override.settings ?? {}))
        .filter(
            (key) =>
                !CATEGORIZED_KEYS.has(key) &&
                !FINGERPRINT_EXCLUDED_KEYS.has(key) &&
                overrideDiffersOnKey(override, appNotification, key),
        )
        .sort();

export const overriddenSettingLabels = (
    override: StepNotificationConfigOverride,
    appNotification: CommonNotification,
): string[] => Array.from(new Set(overriddenSettingKeys(override, appNotification).map(settingLabel)));

export const hasSettingsOverride = (
    override: StepNotificationConfigOverride,
    appNotification: CommonNotification,
): boolean => overriddenSettingKeys(override, appNotification).length > 0;

export const computeOverrideIndicators = (
    override: StepNotificationConfigOverride,
    appNotification: CommonNotification,
) => ({
    hasChannelOverride: overrideHasChannelDifference(override, appNotification),
    hasMentionOverride: overrideHasMentionDifference(override, appNotification),
    hasTriggerOverride: hasTriggerOverride(override, appNotification),
    hasSettingsOverride: hasSettingsOverride(override, appNotification),
});

export const notificationRowIndicators = (notification: CommonNotification) => {
    const overrides = notification.stepConfigOverrides ?? [];
    const anyOverride = (
        check: (override: StepNotificationConfigOverride, appNotification: CommonNotification) => boolean,
    ) => overrides.some((override) => check(override, notification));
    return {
        hasChannelOverride: (notification.channels?.length ?? 0) > 0 || anyOverride(overrideHasChannelDifference),
        hasMentionOverride:
            (notification.mentionGroups?.length ?? 0) > 0 ||
            (notification.mentionUsers?.length ?? 0) > 0 ||
            anyOverride(overrideHasMentionDifference),
        hasTriggerOverride: anyOverride(hasTriggerOverride),
        hasSettingsOverride: anyOverride(hasSettingsOverride),
    };
};

const DESTINATION_LABELS: Record<string, string> = {
    [NotificationDestination.Slack]: 'Slack/Teams',
    [NotificationDestination.Email]: 'Email',
};

export const overriddenDestinationLabel = (
    override: StepNotificationConfigOverride,
    appNotification: CommonNotification,
): string | null => {
    if (!DESTINATION_SETTINGS_KEYS.some((key) => overrideDiffersOnKey(override, appNotification, key))) {
        return null;
    }
    const destinations = (mergeOverrideIntoNotification(appNotification, override).notificationChannels ??
        []) as string[];
    const labels = Array.from(
        new Set(destinations.map((destination) => DESTINATION_LABELS[destination] ?? destination)),
    );
    return labels.join(' + ') || DESTINATION_LABELS[NotificationDestination.Slack];
};

export const mergeOverrideIntoNotification = (
    appNotification: CommonNotification,
    override: StepNotificationConfigOverride | null | undefined,
): CommonNotification => {
    if (!override) {
        // channels start empty: an empty list follows the app-level channels
        return { ...appNotification, channels: [] };
    }
    if (override.overriddenFields) {
        const merged: Record<string, unknown> = { ...appNotification, enabled: override.enabled, channels: [] };
        const settings = (override.settings ?? {}) as Record<string, unknown>;
        const mentionOverridden = override.overriddenFields.some((key) => MENTION_CHOICE_KEYS.includes(key));
        const overriddenKeys =
            mentionOverridden ?
                Array.from(new Set([...override.overriddenFields, ...MENTION_CHOICE_KEYS]))
            :   override.overriddenFields;
        overriddenKeys.forEach((key) => {
            if (key === 'channels') {
                merged.channels = override.channels ?? [];
                merged.usePrimaryChannels = !!override.usePrimaryChannels;
                return;
            }
            if (key in settings) {
                merged[key] = settings[key];
            } else {
                // omitempty dropped a cleared value; the form falls back to its empty default
                delete merged[key];
            }
        });
        if (override.overriddenFields.includes('conditionGroups')) {
            LEGACY_THRESHOLD_KEYS.filter((key) => !override.overriddenFields?.includes(key)).forEach(
                (key) => delete merged[key],
            );
        }
        return merged as unknown as CommonNotification;
    }
    return {
        ...appNotification,
        ...(override.settings ?? {}),
        enabled: override.enabled,
        channels: override.channels ?? [],
    };
};

export const effectiveMentionLabels = (
    appNotification: CommonNotification,
    override: StepNotificationConfigOverride,
): string[] => {
    const merged = mergeOverrideIntoNotification(appNotification, override);
    return [
        ...(merged.mentionGroups ?? []),
        ...(merged.mentionUsers ?? []),
        ...(merged.mentionReleasePilot ? ['Release pilot'] : []),
        ...(merged.emailsToInclude ?? []),
    ];
};

export const sortStepConfigOverrides = (
    overrides: StepNotificationConfigOverride[],
): StepNotificationConfigOverride[] =>
    [...overrides].sort(
        (a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? '') || a.stepConfigId.localeCompare(b.stepConfigId),
    );

export const deriveOverrideEntries = (
    savedOverrides: StepNotificationConfigOverride[],
): NotificationOverrideEntry[] => {
    const entries: NotificationOverrideEntry[] = [];
    const entryIndexByGroupName: Record<string, number> = {};
    sortStepConfigOverrides(savedOverrides).forEach((override) => {
        const groupName = override.groupName ?? '';
        if (groupName && entryIndexByGroupName[groupName] !== undefined) {
            const entry = entries[entryIndexByGroupName[groupName]];
            entry.stepConfigIds.push(override.stepConfigId);
            entry.savedStepConfigIds.push(override.stepConfigId);
            return;
        }
        if (groupName) {
            entryIndexByGroupName[groupName] = entries.length;
        }
        entries.push({
            key: `override-${entries.length}`,
            stepConfigIds: [override.stepConfigId],
            savedStepConfigIds: [override.stepConfigId],
            groupName,
            savedGroupName: groupName,
            override,
            saved: true,
        });
    });
    return entries;
};

export const applyStepSelectionChange = (
    entries: NotificationOverrideEntry[],
    editedKey: string,
    selected: string[],
): NotificationOverrideEntry[] => {
    const edited = entries.find((entry) => entry.key === editedKey);
    if (!edited) {
        return entries;
    }
    const added = selected.filter((id) => !edited.stepConfigIds.includes(id));

    return entries
        .map((entry) => {
            if (entry.key === editedKey) {
                return { ...entry, stepConfigIds: selected };
            }
            if (added.some((id) => entry.stepConfigIds.includes(id))) {
                return { ...entry, stepConfigIds: entry.stepConfigIds.filter((id) => !added.includes(id)) };
            }
            return entry;
        })
        .filter((entry) => entry.key === editedKey || entry.stepConfigIds.length > 0);
};

const FINGERPRINT_EXCLUDED_KEYS = new Set([
    'channels',
    'usePrimaryChannels',
    'enabled',
    'hourOfDay',
    'conditionGroupsField',
    'mentionType',
]);

const comparableKey = (key: string, canonicalSettingKeys?: string[]): boolean =>
    !FINGERPRINT_EXCLUDED_KEYS.has(key) && (!canonicalSettingKeys?.length || canonicalSettingKeys.includes(key));

const fingerprintPart = (value: unknown) => (isEmptyFormValue(value) ? null : value);

export interface OverrideDeviation {
    enabled: boolean;
    channels: string[];
    sendsToPrimaryChannels: boolean;
    parts: Record<string, unknown>;
}

export const deviationIsNoOp = (deviation: OverrideDeviation, appNotification: CommonNotification): boolean =>
    deviation.enabled === (appNotification.enabled !== false) &&
    deviation.channels.length === 0 &&
    !deviation.sendsToPrimaryChannels &&
    Object.keys(deviation.parts).length === 0;

export const overrideConfigDeviation = (
    override: StepNotificationConfigOverride,
    appNotification: CommonNotification,
    canonicalSettingKeys?: string[],
): OverrideDeviation => {
    const settings = (override.settings ?? {}) as Record<string, unknown>;
    const parts: Record<string, unknown> = {};
    const keys =
        override.overriddenFields ?
            override.overriddenFields.filter((key) => key !== 'channels')
        :   Object.keys(settings);
    [...keys].sort().forEach((key) => {
        if (!comparableKey(key, canonicalSettingKeys)) {
            return;
        }
        if (overrideDiffersOnKey(override, appNotification, key)) {
            parts[key] = fingerprintPart(settings[key]);
        }
    });
    return {
        enabled: override.enabled,
        channels: (override.channels ?? []).map((channel) => channel.id).sort(),
        sendsToPrimaryChannels: overrideSendsToPrimaryChannels(override, appNotification),
        parts,
    };
};

export const pendingOverrideDeviation = (
    formValues: Record<string, unknown>,
    appNotification: CommonNotification,
    canonicalSettingKeys?: string[],
    baseline?: Record<string, unknown> | null,
): OverrideDeviation => {
    const parts: Record<string, unknown> = {};
    Object.keys(formValues)
        .sort()
        .forEach((key) => {
            if (!comparableKey(key, canonicalSettingKeys)) {
                return;
            }
            const value = formValues[key];
            const appValue = baseline ? baseline[key] : withFormDefault(key, appNotification[key]);
            if (isEmptyFormValue(value) && isEmptyFormValue(appValue)) {
                return;
            }
            if (!isEqualWith(value, appValue, looseFormValueCompare)) {
                parts[key] = fingerprintPart(value);
            }
        });
    return {
        enabled: formValues.enabled !== false,
        channels: [...((formValues.channels as string[] | undefined) ?? [])].sort(),
        sendsToPrimaryChannels: sendsToPrimaryChannels(formValues.usePrimaryChannels === true, appNotification),
        parts,
    };
};

export const overrideConfigFingerprint = (
    override: StepNotificationConfigOverride,
    appNotification: CommonNotification,
    canonicalSettingKeys?: string[],
): string => JSON.stringify(overrideConfigDeviation(override, appNotification, canonicalSettingKeys));

export const pendingOverrideConfigFingerprint = (
    formValues: Record<string, unknown>,
    appNotification: CommonNotification,
    canonicalSettingKeys?: string[],
    baseline?: Record<string, unknown> | null,
): string => JSON.stringify(pendingOverrideDeviation(formValues, appNotification, canonicalSettingKeys, baseline));

export const overriddenFieldsForDeviation = (deviation: OverrideDeviation): string[] => [
    ...Object.keys(deviation.parts).sort(),
    ...(deviation.channels.length > 0 || deviation.sendsToPrimaryChannels ? ['channels'] : []),
];

const normalizedGroupName = (name: string) => name.trim().toLowerCase();

export const defaultGroupNames = (entries: NotificationOverrideEntry[]): Record<string, string> => {
    const used = new Set(entries.map((entry) => normalizedGroupName(entry.groupName)).filter(Boolean));
    const names: Record<string, string> = {};
    let next = 1;
    entries.forEach((entry) => {
        if (entry.stepConfigIds.length < 2 || entry.groupName.trim()) {
            return;
        }
        while (used.has(normalizedGroupName(`Override group ${next}`))) {
            next++;
        }
        names[entry.key] = `Override group ${next}`;
        used.add(normalizedGroupName(names[entry.key]));
    });
    return names;
};

export const isGroupNameTaken = (entries: NotificationOverrideEntry[], key: string, name: string): boolean => {
    const target = normalizedGroupName(name);
    if (!target) {
        return false;
    }
    const defaults = defaultGroupNames(entries);
    return entries.some(
        (entry) =>
            entry.key !== key &&
            entry.stepConfigIds.length > 1 &&
            normalizedGroupName(entry.groupName || defaults[entry.key] || '') === target,
    );
};
